import { useQuery } from '@tanstack/react-query';
import { fetchCasePnrGroupsPage } from './pnr-groups';
import { foldHotelBoard } from '@/components/hotels/build-hotel-board';
import type { HotelBoardBooking, IHotelBoard } from '@/components/hotels/types';

// CASE-SCOPE adapter for the reusable hotel-occupancy board — the case-detail
// counterpart of the disruption `buildHotelBoard`/`useHotelBoard`. Walks the
// case's PNR groups (the same `fetchCasePnrGroupsPage` the operation-center roster
// uses), keeps the groups that landed a HOTEL, folds them per `hotelUrn`, and hands
// them to the SAME scope-neutral `foldHotelBoard` for enrichment. So the case
// Allocation tab renders the IDENTICAL `HotelOccupancyBoard` the disruption Hotels
// tab does, scoped to one case. Built entirely from existing endpoints; no new
// backend.

const MAX_PAGES = 30;

export async function buildCaseHotelBoard(caseUrn: string): Promise<IHotelBoard> {
  const byHotel = new Map<string, HotelBoardBooking[]>();
  const caseUrns = new Set<string>();
  let pendingPnrs = 0;
  let cursor: string | undefined;
  let pages = 0;
  let truncated = false;

  for (;;) {
    const page = await fetchCasePnrGroupsPage(caseUrn, {
      ...(cursor ? { cursor } : {}),
    });
    for (const g of page.items) {
      if (g.accommodationKind !== 'HOTEL') continue;
      if (g.hotelUrn) {
        const list = byHotel.get(g.hotelUrn) ?? [];
        list.push(g);
        byHotel.set(g.hotelUrn, list);
        if (g.caseUrn) caseUrns.add(g.caseUrn);
      } else {
        pendingPnrs += 1; // needs a hotel, not yet allocated
      }
    }
    pages += 1;
    if (!page.nextCursor) break;
    if (pages >= MAX_PAGES) {
      truncated = true;
      break;
    }
    cursor = page.nextCursor;
  }
  // caseUrns always contains this case; ensure it's present even when no HOTEL
  // group carried it (e.g. all pending) so the reservation join still runs.
  caseUrns.add(caseUrn);

  return foldHotelBoard({
    groupsByHotel: byHotel,
    caseUrns,
    pendingPnrs,
    truncated,
    scopeUrn: caseUrn,
  });
}

/**
 * The hotel-occupancy board for a single case — hotels → their booked PNRs →
 * guests, enriched with stars / distance / rate. Lazy (only when `enabled`);
 * cached 30s. Built from the case's PNR groups, so it reflects exactly what the
 * Passengers roster shows as booked.
 */
export function useCaseHotelBoard(caseUrn: string | undefined, enabled = true) {
  return useQuery<IHotelBoard>({
    queryKey: ['cases', caseUrn, 'hotel-board'],
    queryFn: () => buildCaseHotelBoard(caseUrn!),
    enabled: !!caseUrn && enabled,
    staleTime: 30_000,
  });
}
